/**
 * CandidatesTab — the Candidates tab of Search Detail: stage chips, the
 * filters, the pager and the state behind `CandidatesTable`.
 *
 * Grown from `AllCandidatesPanel` (the Dashboard's All Candidates panel) on
 * 2026-10-06 (epic `2026-10-06-search-candidates-reports`, sub-task 02). The
 * panel stayed beside this file while the `search-candidates` flag was off on
 * prod, and was deleted with its table when the flag was released on every
 * stage (2026-10-07). The two read the SAME URL parameters but DIVERGED IN
 * DEFAULTS, which is what an old bookmark of the Dashboard panel now meets:
 *
 *   - a missing `order` was `asc` in the panel; here it is `desc` for the
 *     ladder (`defaultOrderFor`) and `asc` elsewhere — and this tab
 *     OMITS `order` from the URL exactly when it equals that default;
 *   - `?sort=stage` / `?sort=highest_stage` were normalised to their `_alpha`
 *     twins by the panel (Trello #711 CL3); here `stage` is the ladder key and
 *     `highest_stage` reads as the default since its column went (2026-10-08);
 *   - the default sort was `name` there and is `stage` here.
 *
 * A panel bookmark carries no `?tab=`, so it opens on the Dashboard, which has
 * no table any more; its filter params ride along unused until the reader
 * opens this tab, where they apply with this tab's defaults.
 *
 * WHY THE STATE IS IN THE URL. A filtered, sorted view of a search's candidates
 * is a thing people send each other, and it has to survive a reload. That is
 * the same convention `?tab=` follows on this page and the reason Back toggles
 * tabs rather than leaving. Text filters write to the URL DEBOUNCED and with
 * `replace`, so typing a company name does not leave twelve history entries
 * between the reader and the page they came from; the chips and selects write
 * immediately, because a click is a decision.
 *
 * An unrecognised value in a URL parameter reads as its default rather than as
 * an error — `?sort=nope` shows the default ordering, exactly as an unknown
 * `?tab=` shows the Dashboard. The API rejects an unknown `sort` with a 422 on
 * purpose (a silently unsorted table looks like it works), and this is the
 * client half of that: the parameter is user-editable and arrives in shared
 * links, so a typo should not blank the table.
 *
 * THE DEFAULT ORDER IS THE LADDER, DESCENDING — furthest-along first (Jose,
 * 2026-10-05). The panel defaulted to A-Z by name and normalised an old
 * `?sort=stage` to `stage_alpha` (Trello #711 CL3); this tab does neither. Its
 * Stage header sorts by the ladder, so `stage` is both the default and a lit
 * header, and `stage_alpha` is still accepted from a link but no header offers
 * it. The default is the ABSENCE of `sort` and `order` in the URL, so the
 * canonical link to the tab stays `?tab=candidates`.
 *
 * THE PAGE SIZE IS THE WEB'S CHOICE NOW. The panel sent no `page_size` and the
 * server's default (10) was the single source of truth; this tab sends
 * `candidatesPageSize()` — 10 local, 20 dev, 50 prod, `VITE_LIST_PAGE_SIZE`
 * override (5 in E2E) — the per-stage rule every other list follows (epic
 * decision D6). The server default stays 10 for a caller that sends nothing.
 *
 * EVERY filter and the sort are applied SERVER-side. The endpoint exists so the
 * browser never receives 1299 rows, and filtering here would require exactly
 * that.
 *
 * THE CANDIDATE PANEL IS IN THE URL TOO (`?candidate=<candidacy_id>`,
 * sub-task 04), so a panel deep-links and Back closes it. Opening PUSHES an
 * entry marked in its history state; ‹ › REPLACE; closing a panel opened here
 * goes BACK to the entry below, and closing one that arrived in a link
 * REPLACES — the People Lists panel's rule (`usePersonPanelUrl`), rebuilt
 * here rather than imported across the feature boundary. The filters, the
 * sort and the page never touch `candidate`, and `candidate` never enters the
 * candidates query, so opening a panel refetches nothing.
 *
 * Design-system token utilities only; no inline `style`; English copy only.
 */

import type {
  SearchCandidateSortKey,
  SearchCandidateSortOrder,
  SearchReportBucket,
} from "@nu-assist/contracts";
import { candidatesPageSize } from "@nu-assist/shared-fe";
import { Button } from "@nu-assist/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { AvatarsClient } from "../../../clients/avatars.client";
import {
  CANDIDATE_REPORT_KINDS,
  type CandidateReportsClient,
} from "../../../clients/candidateReports.client";
import type { SearchReportClient } from "../../../clients/searchReport.client";
import { RetryButton } from "../../../components/RetryButton";
import { TablePagination } from "../../../components/TablePagination";
import { useResolveThriveAvatars } from "../../../components/avatars/useResolveThriveAvatars";
import { StageChips } from "../../../components/stages/StageChips";
import { CandidateReportPanel } from "./CandidateReportPanel";
import { CandidatesTable } from "./CandidatesTable";
import { useSearchCandidates } from "./useSearchCandidates";

/** How long to wait after the last keystroke before asking the server. */
const FILTER_DEBOUNCE_MS = 300;

/**
 * Which `?sort=` values this tab accepts from a URL: `true` for the keys it
 * honours, `false` for the API's keys it does not.
 *
 * A `Record` keyed on the contract type rather than an array, so `tsc` catches
 * drift in BOTH directions: a key the backend drops fails as an unknown
 * property, and one it adds fails as a missing one. An array typed
 * `SearchCandidateSortKey[]` would only catch the first.
 *
 * `email`, `highest_stage` and `highest_stage_alpha` went with their columns
 * (Jose, 2026-10-08, epic `2026-10-08-candidates-polish-thrive-avatar-worker`,
 * sub-task 01): the API still orders by them, but a shared link carrying one
 * now opens in the default order, as an unknown value does, rather than in an
 * order no header on screen can name.
 */
const SORT_KEYS: Record<SearchCandidateSortKey, boolean> = {
  name: true,
  email: false,
  title: true,
  company: true,
  linkedin_url: true,
  stage: true,
  stage_alpha: true,
  highest_stage: false,
  highest_stage_alpha: false,
  created_at: true,
  stage_since: true,
};

/** The ladder, furthest-along first — see the module note. */
export const DEFAULT_SORT: SearchCandidateSortKey = "stage";
export const DEFAULT_ORDER: SearchCandidateSortOrder = "desc";

/** Presence filters are tri-state in the URL: absent, `has`, or `none`. */
type PresenceFilter = "any" | "has" | "none";

// The two report filters cover a whole group each (epic
// 2026-10-22-candidate-reports-four-kinds): the API's `has_assessment` /
// `has_references` match any report of the group, because the read always
// sends the four kinds as `report_kinds` (released 2026-10-26, D5). The chips
// say so in their tooltips.
export const ASSESSMENT_FILTER_TITLE =
  "Has: a recruiter or a client assessment in Thrive. Missing: neither.";
export const REFERENCES_FILTER_TITLE =
  "Has: official or backchannel references for this search in Thrive. Missing: neither.";

function presenceFrom(raw: string | null): PresenceFilter {
  return raw === "has" || raw === "none" ? raw : "any";
}

function presenceToBool(value: PresenceFilter): boolean | undefined {
  return value === "any" ? undefined : value === "has";
}

/** A value that only settles `delayMs` after it last changed. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

interface TextFilters {
  name: string;
  title: string;
  company: string;
}

export interface CandidatesTabProps {
  searchId: string;
  client: SearchReportClient;
  /** The avatar store's reads (`/avatars/resolve-thrive` and friends) for the
   *  rows' photos. Omitted → initials only, nothing is asked (sub-task 05). */
  avatarsClient?: AvatarsClient;
  /** `report.current_stage_distribution` — the chips' labels and counts. */
  stageDistribution: readonly SearchReportBucket[];
  /** "Now" for the In stage column; injectable so a test can pin a number. */
  now?: Date;
  /** The stored-report reads (sub-task 04). Omitted → the Reports column is em
   *  dashes and rows do not open the candidate panel. */
  reportsClient?: CandidateReportsClient;
  /** The search's name, for the panel's "From “Candidates · …”" line. */
  searchName?: string | null;
}

/** The history-state key marking the entry an in-page panel open pushed. */
const PANEL_PUSHED_KEY = "candidatePanelPushed";

function historyRecord(state: unknown): Record<string, unknown> {
  return typeof state === "object" && state !== null ? (state as Record<string, unknown>) : {};
}

/**
 * A multi-select over the stage values this search actually has.
 *
 * A native `<details>` disclosure with checkboxes inside rather than a Radix
 * menu: the option set is closed and small (~11 live stages), and `<details>`
 * gives keyboard operation and the open/closed state for free. The current
 * stage ALSO has chips above the toolbar; this menu is the complete control
 * (it offers Highest stage too) and the chips are the fast one.
 */
function StageMultiSelect({
  label,
  options,
  selected,
  onToggle,
  testId,
}: {
  label: string;
  options: readonly string[];
  selected: readonly string[];
  onToggle: (value: string) => void;
  testId: string;
}) {
  // Rendered even with no options, so the toolbar does not reflow as the
  // control appears between a `generating` answer and a `ready` one. Opening it
  // then says so rather than showing an empty box the reader has to interpret.
  return (
    <details data-testid={testId} className="relative">
      <summary
        className={`cursor-pointer list-none rounded-sm border border-border px-2 py-1 text-caption ${
          selected.length > 0 ? "border-accent text-accent" : "text-text-secondary"
        }`}
      >
        {label}
        {selected.length > 0 ? ` (${selected.length})` : ""}
      </summary>
      <div className="absolute z-20 mt-1 flex max-h-64 min-w-[12rem] flex-col gap-1 overflow-auto rounded-sm border border-border bg-surface p-2 shadow-md">
        {options.length === 0 ? (
          <p className="text-caption text-text-secondary">No stages yet.</p>
        ) : (
          options.map((option) => (
            <label key={option} className="flex items-center gap-2 text-caption text-text">
              <input
                type="checkbox"
                data-testid={`${testId}-option`}
                data-value={option}
                checked={selected.includes(option)}
                onChange={() => onToggle(option)}
              />
              {option}
            </label>
          ))
        )}
      </div>
    </details>
  );
}

function PresenceSelect({
  label,
  value,
  onChange,
  testId,
  title,
}: {
  label: string;
  value: PresenceFilter;
  onChange: (next: PresenceFilter) => void;
  testId: string;
  /** What "Has" counts, when the label alone does not say. */
  title?: string;
}) {
  return (
    <label title={title} className="flex items-center gap-1 text-caption text-text-secondary">
      {label}
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value as PresenceFilter)}
        className="rounded-sm border border-border bg-surface px-2 py-1 text-caption text-text"
      >
        <option value="any">Any</option>
        <option value="has">Has</option>
        <option value="none">Missing</option>
      </select>
    </label>
  );
}

export function CandidatesTab({
  searchId,
  client,
  avatarsClient,
  stageDistribution,
  now,
  reportsClient,
  searchName,
}: CandidatesTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Scalars, not one `applied` object: an object literal is a new identity every
  // render, so a hook that depended on it would re-run forever.
  const appliedName = searchParams.get("name") ?? "";
  const appliedTitle = searchParams.get("title") ?? "";
  const appliedCompany = searchParams.get("company") ?? "";
  // `order` defaults per SORT: the ladder opens descending (furthest-along
  // first), every other column ascending — "sort by company" means A-Z. The URL
  // carries `order` only when it differs from that column's own default.
  const { sort, order } = candidatesSortFromParams(searchParams);
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const stages = searchParams.getAll("stage");
  const highestStages = searchParams.getAll("highest_stage");
  const linkedin = presenceFrom(searchParams.get("linkedin"));
  const email = presenceFrom(searchParams.get("email"));
  const assessment = presenceFrom(searchParams.get("assessment"));
  const references = presenceFrom(searchParams.get("references"));
  const pageSize = candidatesPageSize();

  // Draft text, so the inputs stay responsive while the request is debounced.
  const [draft, setDraft] = useState<TextFilters>({
    name: appliedName,
    title: appliedTitle,
    company: appliedCompany,
  });
  // What this tab last WROTE to the URL. Without it there is no way to tell a
  // URL change we caused from one the reader caused (Back, or opening a shared
  // link), and adopting our own write back into the draft would fight the
  // typing it came from.
  const lastPushed = useRef<TextFilters>({
    name: appliedName,
    title: appliedTitle,
    company: appliedCompany,
  });
  const {
    name: settledName,
    title: settledTitle,
    company: settledCompany,
  } = useDebounced(draft, FILTER_DEBOUNCE_MS);

  useEffect(() => {
    const changedElsewhere =
      appliedName !== lastPushed.current.name ||
      appliedTitle !== lastPushed.current.title ||
      appliedCompany !== lastPushed.current.company;
    if (changedElsewhere) {
      const adopted = { name: appliedName, title: appliedTitle, company: appliedCompany };
      lastPushed.current = adopted;
      setDraft(adopted);
    }
  }, [appliedName, appliedTitle, appliedCompany]);

  useEffect(() => {
    if (
      settledName === appliedName &&
      settledTitle === appliedTitle &&
      settledCompany === appliedCompany
    ) {
      return;
    }
    lastPushed.current = { name: settledName, title: settledTitle, company: settledCompany };
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        const settled: TextFilters = {
          name: settledName,
          title: settledTitle,
          company: settledCompany,
        };
        for (const [key, value] of Object.entries(settled)) {
          if (value.trim()) next.set(key, value.trim());
          else next.delete(key);
        }
        // Any change to the filters invalidates the page number: page 9 of a
        // set that now has 12 rows is an empty table with a pager that says
        // otherwise.
        next.delete("page");
        return next;
      },
      // `replace`, not push: a debounced keystroke is not a navigation, and
      // pushing one per word would bury the page the reader arrived from.
      { replace: true }
    );
  }, [
    settledName,
    settledTitle,
    settledCompany,
    appliedName,
    appliedTitle,
    appliedCompany,
    setSearchParams,
  ]);

  /** Mutate the URL params, resetting the page unless told otherwise. */
  function updateParams(
    mutate: (next: URLSearchParams) => void,
    { keepPage = false }: { keepPage?: boolean } = {}
  ) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      mutate(next);
      if (!keepPage) next.delete("page");
      return next;
    });
  }

  function toggleSort(key: SearchCandidateSortKey) {
    updateParams((next) => {
      // Pressing the ACTIVE column flips the direction; pressing another one
      // starts it at its own default — descending for the ladder (furthest
      // along first), ascending for everything else.
      const flipping = key === sort;
      const nextOrder: SearchCandidateSortOrder = flipping
        ? order === "asc"
          ? "desc"
          : "asc"
        : defaultOrderFor(key);
      if (key === DEFAULT_SORT) next.delete("sort");
      else next.set("sort", key);
      if (nextOrder === defaultOrderFor(key)) next.delete("order");
      else next.set("order", nextOrder);
    });
  }

  function toggleStage(param: "stage" | "highest_stage", value: string) {
    updateParams((next) => {
      const current = next.getAll(param);
      next.delete(param);
      const kept = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      for (const v of kept) next.append(param, v);
    });
  }

  function setPresence(
    param: "linkedin" | "email" | "assessment" | "references",
    value: PresenceFilter
  ) {
    updateParams((next) => {
      if (value === "any") next.delete(param);
      else next.set(param, value);
    });
  }

  function goToPage(target: number) {
    updateParams(
      (next) => {
        if (target <= 1) next.delete("page");
        else next.set("page", String(target));
      },
      { keepPage: true }
    );
  }

  function clearFilters() {
    setDraft({ name: "", title: "", company: "" });
    lastPushed.current = { name: "", title: "", company: "" };
    updateParams((next) => {
      for (const key of [
        "name",
        "title",
        "company",
        "stage",
        "highest_stage",
        "linkedin",
        "email",
        "assessment",
        "references",
      ]) {
        next.delete(key);
      }
    });
  }

  const { query, pollingExhausted, retry } = useSearchCandidates({
    client,
    searchId,
    query: {
      page,
      pageSize,
      sort,
      order,
      name: appliedName,
      title: appliedTitle,
      company: appliedCompany,
      stage: stages,
      highestStage: highestStages,
      hasLinkedin: presenceToBool(linkedin),
      hasEmail: presenceToBool(email),
      hasAssessment: presenceToBool(assessment),
      hasReferences: presenceToBool(references),
      reportKinds: CANDIDATE_REPORT_KINDS,
    },
  });

  const payload = query.data;
  const filtered =
    Boolean(appliedName || appliedTitle || appliedCompany) ||
    stages.length > 0 ||
    highestStages.length > 0 ||
    linkedin !== "any" ||
    email !== "any" ||
    assessment !== "any" ||
    references !== "any";

  const total = payload?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const items = payload?.status === "ready" ? (payload.items ?? []) : [];
  // The rows' photos, resolved AFTER the page renders: the table paints with
  // initials and the photos fill in as the store answers (sub-task 05).
  const avatarUrls = useResolveThriveAvatars(avatarsClient, items);
  // A `page` past the end — a dev link (`page=3` at 20 per page) opened on
  // prod (50 per page), or a filter that shrank the set under a bookmarked
  // page. The server answers an empty page over a non-zero total. CLAMP to
  // the last page (a `replace`: the bad page is not a place worth going Back
  // to) rather than word the empty table — a sentence left Prev pointing at
  // `page - 1`, which is usually still past the end (PR #1009 iter 2, C #4).
  //
  // ONLY ON THIS KEY'S OWN DATA. `useSearchCandidates` keeps the PREVIOUS
  // key's payload as placeholder while a new key loads, so judging `page`
  // against a placeholder `total` judges it against another query's set:
  // Back from a one-page filtered view to `?page=3` saw `total: 5`, clamped
  // to page 1 with `replace`, and lost both the page and the history entry
  // (iter 3, C #1). `isPlaceholderData` is the right guard here, not
  // `isFetching` as in `ImportsListView`: a key that HAS cached data answers
  // with its own total, and this query never polls a `ready` answer, so the
  // stale-revalidation window that forced `isFetching` there does not exist.
  const pastTheEnd =
    !query.isPlaceholderData && payload?.status === "ready" && total > 0 && page > lastPage;
  // Empty rows that are not an empty table, read off the PAYLOAD rather than
  // the URL: (a) the past-the-end answer itself — `items: []` over a non-zero
  // total is only ever that, since an in-range page of a non-empty set has a
  // row (iter 4, A §2.2: the former "page past the last" clause was implied
  // by these three); (b) ANY empty placeholder — the previous key's answer
  // standing in while the new key loads, whether it was a past-the-end page
  // or a filtered-to-zero one (`total: 0`) the reader has just cleared (iter
  // 4, C #2). Both are the table LOADING its real page.
  const loadingBehindEmptyRows =
    (payload?.status === "ready" && total > 0 && items.length === 0) ||
    (query.isPlaceholderData && items.length === 0);
  useEffect(() => {
    if (!pastTheEnd) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (lastPage <= 1) next.delete("page");
        else next.set("page", String(lastPage));
        return next;
      },
      { replace: true }
    );
  }, [pastTheEnd, lastPage, setSearchParams]);

  // The candidate panel (sub-task 04). `?candidate=` is the single source of
  // truth; see the module note for the history rules.
  const openCandidacyId = reportsClient ? searchParams.get("candidate") : null;
  const pushedHere = historyRecord(location.state)[PANEL_PUSHED_KEY] === true;
  // The row to give focus back to on close: the one the panel showed LAST.
  const returnFocusTo = useRef<string | null>(null);
  if (openCandidacyId) returnFocusTo.current = openCandidacyId;

  const writeCandidate = useCallback(
    (candidacyId: string | null, replace: boolean, state: unknown) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (candidacyId) next.set("candidate", candidacyId);
          else next.delete("candidate");
          return next;
        },
        { replace, state }
      );
    },
    [setSearchParams]
  );

  function openCandidate(candidacyId: string) {
    if (openCandidacyId) {
      writeCandidate(candidacyId, true, location.state ?? null);
      return;
    }
    writeCandidate(candidacyId, false, {
      ...historyRecord(location.state),
      [PANEL_PUSHED_KEY]: true,
    });
  }

  function closeCandidate() {
    if (pushedHere) navigate(-1);
    else {
      const { [PANEL_PUSHED_KEY]: _mark, ...rest } = historyRecord(location.state);
      writeCandidate(null, true, Object.keys(rest).length > 0 ? rest : null);
    }
  }

  // Focus back to the last-shown row's name once the panel is gone. After the
  // commit that removed it, so the drawer's own focus handling is over.
  const panelOpen = openCandidacyId !== null;
  const wasOpen = useRef(panelOpen);
  const tabRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (wasOpen.current && !panelOpen && returnFocusTo.current) {
      const id = returnFocusTo.current;
      const row = Array.from(
        tabRef.current?.querySelectorAll<HTMLElement>('[data-testid="candidates-row"]') ?? []
      ).find((tr) => tr.dataset.candidacyId === id);
      // Off the page (a deep link, a filter that dropped the row): the name
      // filter, the first control of the table's toolbar — never <body>.
      const target =
        row?.querySelector<HTMLElement>('[data-testid="candidate-open"]') ??
        tabRef.current?.querySelector<HTMLElement>('[data-testid="candidates-filter-name"]');
      target?.focus();
    }
    wasOpen.current = panelOpen;
  }, [panelOpen]);

  const openIndex = openCandidacyId
    ? items.findIndex((row) => String(row.candidacy_id ?? "") === openCandidacyId)
    : -1;
  const openRow = openIndex >= 0 ? (items[openIndex] ?? null) : null;
  const openContactId = openRow?.contact_id == null ? null : String(openRow.contact_id);
  const rowsLoading = !payload || payload.status !== "ready" || loadingBehindEmptyRows;

  function moveCandidate(offset: number) {
    const target = items[openIndex + offset]?.candidacy_id;
    if (openIndex < 0 || target == null) return;
    writeCandidate(String(target), true, location.state ?? null);
  }

  // Three different empty tables, and they must not share a sentence. "Nothing
  // matches" over a search whose projection has not been composed would be a
  // claim about the data; "still building" over a filtered-to-zero table would
  // be a claim about the system. A chip that took every row lands on the
  // second sentence: the filter emptied the table, not the search. A page past
  // the end, or any placeholder standing in for a key still loading, is not
  // an empty table at all and reads as loading — the frame between
  // `useEffect`s is painted, so the sentence must be right in it too (iter 3,
  // A §2.1 / C #2; iter 4, C #2). A request that FAILED with nothing to show
  // says nothing in the cell: the banner above it carries the error and the
  // retry, and "Loading candidates…" under it was a promise nothing kept
  // (iter 4, C #4). Precedent: `ImportsListView`'s clamp note.
  const emptyMessage =
    query.isError && !payload
      ? ""
      : !payload || loadingBehindEmptyRows
        ? "Loading candidates…"
        : payload.status !== "ready"
          ? "Building this table from Thrive. It usually takes a few seconds."
          : filtered
            ? "No candidate matches these filters."
            : "No candidates on this search yet.";

  return (
    <div ref={tabRef} data-testid="candidates-tab" className="flex flex-col gap-3">
      <StageChips
        distribution={stageDistribution}
        selected={stages}
        onToggle={(value) => toggleStage("stage", value)}
      />

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          data-testid="candidates-filter-name"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Name"
          aria-label="Filter by name"
          className="w-40 rounded-sm border border-border bg-surface px-2 py-1 text-caption text-text"
        />
        <input
          type="search"
          data-testid="candidates-filter-title"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          placeholder="Title"
          aria-label="Filter by title"
          className="w-40 rounded-sm border border-border bg-surface px-2 py-1 text-caption text-text"
        />
        <input
          type="search"
          data-testid="candidates-filter-company"
          value={draft.company}
          onChange={(e) => setDraft({ ...draft, company: e.target.value })}
          placeholder="Company"
          aria-label="Filter by company"
          className="w-40 rounded-sm border border-border bg-surface px-2 py-1 text-caption text-text"
        />
        <StageMultiSelect
          label="Stage"
          options={payload?.stage_options ?? []}
          selected={stages}
          onToggle={(value) => toggleStage("stage", value)}
          testId="candidates-filter-stage"
        />
        <StageMultiSelect
          label="Highest stage"
          options={payload?.highest_stage_options ?? []}
          selected={highestStages}
          onToggle={(value) => toggleStage("highest_stage", value)}
          testId="candidates-filter-highest-stage"
        />
        <PresenceSelect
          label="LinkedIn"
          value={linkedin}
          onChange={(value) => setPresence("linkedin", value)}
          testId="candidates-filter-linkedin"
        />
        <PresenceSelect
          label="Email"
          value={email}
          onChange={(value) => setPresence("email", value)}
          testId="candidates-filter-email"
        />
        <PresenceSelect
          label="Assessment"
          value={assessment}
          onChange={(value) => setPresence("assessment", value)}
          testId="candidates-filter-assessment"
          title={ASSESSMENT_FILTER_TITLE}
        />
        <PresenceSelect
          label="References"
          value={references}
          onChange={(value) => setPresence("references", value)}
          testId="candidates-filter-references"
          title={REFERENCES_FILTER_TITLE}
        />
        {filtered ? (
          <Button variant="secondary" data-testid="candidates-clear-filters" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : null}
      </div>

      {payload?.candidates_truncated ? (
        <p data-testid="candidates-truncated" className="text-caption text-warning-ink">
          This search has more candidates than the cached table can hold, so the list below is short
          of the full set. The charts on the Dashboard still count every candidate.
        </p>
      ) : null}

      {query.isError && !payload ? (
        <div
          data-testid="candidates-error"
          className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
        >
          <output>Couldn’t load this search’s candidates.</output>
          <RetryButton onClick={retry} busy={query.isFetching} />
        </div>
      ) : null}

      {pollingExhausted ? (
        <div
          data-testid="candidates-poll-exhausted"
          className="flex flex-wrap items-center gap-3 rounded-md border border-warning-strong bg-warning-muted px-4 py-3 text-body text-warning-ink"
        >
          <output>
            This table is taking longer than expected, so we stopped checking automatically.
          </output>
          <RetryButton onClick={retry} busy={query.isFetching} label="Check again" />
        </div>
      ) : null}

      <CandidatesTable
        rows={items}
        sort={sort}
        order={order}
        onSort={toggleSort}
        emptyMessage={emptyMessage}
        now={now}
        avatarUrls={avatarUrls}
        reports={reportsClient ? { client: reportsClient, searchId } : undefined}
        onOpenCandidate={reportsClient ? openCandidate : undefined}
      />

      {/* The People Lists pager: "<n> per page / <total> total", Prev /
          numbered / Next — collapsing to "Showing N candidates" when the set
          fits one page. Sized off the FILTERED total, which is what `total`
          is, and off the size this tab ASKED for rather than the one echoed
          back, so the pager and the request cannot disagree. */}
      <TablePagination
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={goToPage}
        noun="candidate"
        testId="candidates-pagination"
      />

      {reportsClient && openCandidacyId ? (
        <CandidateReportPanel
          searchId={searchId}
          searchName={searchName}
          client={reportsClient}
          candidacyId={openCandidacyId}
          row={openRow}
          position={openIndex >= 0 ? { index: (page - 1) * pageSize + openIndex, total } : null}
          positionLoading={openIndex < 0 && rowsLoading}
          canPrev={openIndex > 0}
          canNext={openIndex >= 0 && openIndex < items.length - 1}
          onPrev={() => moveCandidate(-1)}
          onNext={() => moveCandidate(1)}
          onClose={closeCandidate}
          avatarUrl={openContactId ? avatarUrls.get(openContactId) : undefined}
          now={now}
        />
      ) : null}
    </div>
  );
}

/**
 * The sort and direction this tab reads from a URL — the same resolution the
 * component applies, exported so `SearchDetailPage`'s "Current view" export
 * can order the document as the table on screen is ordered. The panel does
 * not offer this (its export is in the server's default order), which is the
 * one place the two deliberately differ beyond defaults.
 */
export function candidatesSortFromParams(params: URLSearchParams): {
  sort: SearchCandidateSortKey;
  order: SearchCandidateSortOrder;
} {
  const rawSort = params.get("sort") ?? "";
  // `=== true`, not `in`: a dropped key is `false`, and `in` would also take
  // `?sort=toString` from the prototype.
  const sort =
    SORT_KEYS[rawSort as SearchCandidateSortKey] === true
      ? (rawSort as SearchCandidateSortKey)
      : DEFAULT_SORT;
  const rawOrder = params.get("order");
  const order = rawOrder === "asc" || rawOrder === "desc" ? rawOrder : defaultOrderFor(sort);
  return { sort, order };
}

/**
 * The direction a column opens in when it is pressed from another one, and
 * the one the URL omits for it. The ladder opens furthest-along first, and
 * every other column opens A-Z / oldest first.
 */
export function defaultOrderFor(key: SearchCandidateSortKey): SearchCandidateSortOrder {
  return key === "stage" ? DEFAULT_ORDER : "asc";
}
